import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const title = 'Props';
export const _frontmatter = {};
const layoutProps = {
  title,
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h1 {...{
      "id": "props"
    }}>{`Props`}</h1>
    <p>{`All Rebass components extend the base `}<a parentName="p" {...{
        "href": "/box"
      }}>{`Box`}</a>{` component,
and include `}<a parentName="p" {...{
        "href": "https://styled-system.com"
      }}>{`Styled System`}</a>{` props for ensuring your design constraints are easily accessible,
while allowing you the flexibility to create responsive designs inline in your components.`}</p>
    <h2 {...{
      "id": "sx-prop"
    }}><inlineCode parentName="h2">{`sx`}</inlineCode>{` Prop`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop accepts a style object, with easy access to values from your theme
and the ability to quickly add responsive styles to any property using arrays as values.
If you've used `}<a parentName="p" {...{
        "href": "https://theme-ui.com"
      }}>{`Theme UI`}</a>{` or `}<a parentName="p" {...{
        "href": "https://styled-system.com"
      }}>{`Styled System`}</a>{` before, this will probably seem familiar.
If you've used the Emotion `}<inlineCode parentName="p">{`css`}</inlineCode>{` prop before, the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop works in a similar way,
but allows you to use values from your theme object.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop is built-in and does not require any additional configuration,
which means it will work in any React app, such as Create React App, or in `}<a parentName="p" {...{
        "href": "https://mdxjs.com"
      }}>{`MDX`}</a>{` documents.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<Box
  sx={{
    color: 'primary', // theme.colors.primary
    fontSize: 2,      // theme.fontSizes[2]
    margin: 3,        // theme.space[3]
  }}>
  Hello
</Box>
`}</code></pre>
    <h2 {...{
      "id": "nested-selectors"
    }}>{`Nested Selectors`}</h2>
    <p>{`Use nested objects to apply pseudoclasses and other child selectors within the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<Button
  sx={{
    ':hover': {
      backgroundColor: 'tomato',
    }
  }}>
  Beep
</Button>
`}</code></pre>
    <h2 {...{
      "id": "responsive-styles"
    }}>{`Responsive Styles`}</h2>
    <p>{`Mobile-first responsive styles based on breakpoints defined in your theme can be quickly applied to
any element using arrays as values in the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop, or with any of the shorthand style props below.
The first value in the array will apply to all screen widths, and each value after will be applied from that breakpoint and up.`}</p>
    <p>{`Define breakpoints in your theme using an array of widths.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// example theme
export default {
  breakpoints: [ '40em', '52em', '64em' ],
}
`}</code></pre>
    <p>{`For any individual CSS property, use an array as a value to quickly apply mobile-first responsive styles for that single property.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<Box
  sx={{
    margin: [ 0, 1, 2 ],
  }}
/>
`}</code></pre>
    <p>{`You can also use standard CSS media query syntax for one-off cases or non-mobile-first styles.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<Box
  sx={{
    display: 'block',
    '@media screen and (max-width: 64em)': {
      display: 'none',
    }
  }}
/>
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop uses `}<a parentName="p" {...{
        "href": "https://styled-system.com/css"
      }}>{`Styled System CSS`}</a>{` to pick up values from your theme
and write responsive styles using the array syntax.`}</p>
    <p>{`If you ever need to add styles that do not use values from your theme, use the `}<inlineCode parentName="p">{`css`}</inlineCode>{` prop, which is available on all components, without the need for additional configuration.`}</p>
    <h2 {...{
      "id": "as-prop"
    }}><inlineCode parentName="h2">{`as`}</inlineCode>{` Prop`}</h2>
    <p>{`The underlying HTML element rendered in any component can be overridden by the `}<inlineCode parentName="p">{`as`}</inlineCode>{` prop.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<Heading as='h1'>Hello</Heading>
`}</code></pre>
    <h2 {...{
      "id": "styled-system-props"
    }}>{`Styled System Props`}</h2>
    <p>{`In addition to the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop, several `}<a parentName="p" {...{
        "href": "https://styled-system.com"
      }}>{`Styled System`}</a>{` props are provided as ergonomic shortcuts.
These props allow you to quickly modify styles on a per-element basis throughout your application.
This level of flexibility in your UI component API
makes it easy to use values from your design system's constraints
in situations that you might not have accounted for when creating your components.`}</p>
    <p>{`Because each component is based on the same base Box component,
the props API for your entire design system can stay consistent,
making it easier to `}<em parentName="p">{`learn once, use everywhere.`}</em></p>
    <h2 {...{
      "id": "colors"
    }}>{`Colors`}</h2>
    <p>{`Use the `}<inlineCode parentName="p">{`color`}</inlineCode>{` and `}<inlineCode parentName="p">{`bg`}</inlineCode>{` (or `}<inlineCode parentName="p">{`backgroundColor`}</inlineCode>{`) props to add styles based on the `}<inlineCode parentName="p">{`theme.colors`}</inlineCode>{` scale.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<Box color='white' bg='secondary' p={3}>
  Hello
</Box>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// Keys reference values in the color palette object
<Text color='primary' />

// Background color can be set with the \`bg\` prop
<Button bg='secondary' />

// Raw CSS values that do not map to a key in \`theme.colors\` can be used
<Button bg='tomato' />
`}</code></pre>
    <h2 {...{
      "id": "margin-and-padding"
    }}>{`Margin and Padding`}</h2>
    <p>{`Margin and padding can be added to any element based on the `}<inlineCode parentName="p">{`theme.space`}</inlineCode>{` scale.
The margin and padding props help promote consistency in layout
without the need to add custom margin and padding declarations throughout an application.
The margin and padding props use a shorthand syntax, similar to other OOCSS approaches and many CSS libraries.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<Box
  p={3}
  mx={2}
  my={4}
  color='background'
  bg='primary'>
  Hello
</Box>
`}</code></pre>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`m`}</inlineCode>{`: margin`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`mt`}</inlineCode>{`: margin-top`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`mr`}</inlineCode>{`: margin-right`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`mb`}</inlineCode>{`: margin-bottom`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`ml`}</inlineCode>{`: margin-left`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`mx`}</inlineCode>{`: margin-left and margin-right`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`my`}</inlineCode>{`: margin-top and margin-bottom`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`p`}</inlineCode>{` : padding`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`pt`}</inlineCode>{`: padding-top`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`pr`}</inlineCode>{`: padding-right`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`pb`}</inlineCode>{`: padding-bottom`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`pl`}</inlineCode>{`: padding-left`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`px`}</inlineCode>{`: padding-left and padding-right`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`py`}</inlineCode>{`: padding-top and padding-bottom`}</li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// Numbers reference steps on the spacing scale
// e.g. 8px
<Text m={2} />

// Numbers greater than the length of \`theme.space.length\` are converted to pixels
<Text my={256} />

// Negative values can be used to add negative margins
<Text mx={-2} />

// Strings can be used for other values
<Text mx='auto' />

// Arrays can be used for mobile-first responsive styles
<Text m={[ 0, 1, 2 ]} />
`}</code></pre>
    <h2 {...{
      "id": "typography"
    }}>{`Typography`}</h2>
    <p>{`Use the typography props to change text styles based on values defined in your theme.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<Text
  fontFamily='monospace'
  fontSize={[ 4, 5 ]}
  fontWeight='bold'
  lineHeight='body'>
  Hello
</Text>
`}</code></pre>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`fontSize`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`fontFamily`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`fontWeight`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`lineHeight`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`letterSpacing`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`textAlign`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`fontStyle`}</inlineCode></li>
    </ul>
    <h2 {...{
      "id": "layout"
    }}>{`Layout`}</h2>
    <p>{`Use layout props to change the width, height, display and other values on any element.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<Box
  display='inline-block'
  width={128}
  height={128}
  p={3}
  color='white'
  bg='primary'>
  Hello
</Box>
`}</code></pre>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`width`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`minWidth`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`maxWidth`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`height`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`minHeight`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`maxHeight`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`display`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`size`}</inlineCode>{` (width and height)`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`verticalAlign`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`overflow`}</inlineCode></li>
    </ul>
    <h3 {...{
      "id": "width"
    }}>{`Width`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`width`}</inlineCode>{` prop includes an additional ergonomic shorthand for applying percentage-based widths.
Any fractional number from `}<inlineCode parentName="p">{`0`}</inlineCode>{` to `}<inlineCode parentName="p">{`1`}</inlineCode>{` will be converted to a percentage based width,
allowing you to quickly define responsive grid layouts directly in the components where you need them.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<Box
  width={[
    1,    // 100% width on small screens
    1/2,  // 50% width from the next breakpoint and up
    1/3,  // 33% width from the next breakpoint and up
    1/4   // 25% width for all larger breakpoints
  ]}
/>
`}</code></pre>
    <h2 {...{
      "id": "flexbox"
    }}>{`Flexbox`}</h2>
    <p>{`Use flexbox props to apply layout styles.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<Flex>
  <Box
    flex='1 1 auto'
    p={3}
    color='white'
    bg='primary'>
    Beep
  </Box>
  <Box p={3} color='white' bg='secondary'>
    Boop
  </Box>
</Flex>
`}</code></pre>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`alignItems`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`alignContent`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`justifyItems`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`justifyContent`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`flexWrap`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`flexDirection`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`flex`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`flexGrow`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`flexShrink`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`flexBasis`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`justifySelf`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`alignSelf`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`order`}</inlineCode></li>
    </ul>
    <p>{`For a full reference of available props, see the `}<a parentName="p" {...{
        "href": "/reflexbox#styled-system-props"
      }}>{`Reflexbox docs`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      